import React, { useState, useEffect } from 'react';
import { Clock, ChevronRight } from 'lucide-react';
import { BakingSession, FERMENTATION_PROCESSES } from '../../types/baking';
import { useBakingSessions, useAnalyses } from '../../lib/hooks';
import { auth } from '../../lib/firebase';
import { ImageModal } from './ImageModal';
import { AnalysisModal } from '../analysis/AnalysisModal';
import { AnalysisDetailsModal } from '../analysis/AnalysisDetailsModal';

export function RecentResults() {
  const { bakingSessions } = useBakingSessions(auth.currentUser?.uid);
  const { analyses } = useAnalyses(auth.currentUser?.uid);
  const [selectedSession, setSelectedSession] = useState<BakingSession | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showAnalysisDetails, setShowAnalysisDetails] = useState(false);
  const [selectedSessionForAnalysis, setSelectedSessionForAnalysis] = useState<BakingSession | null>(null);
  const [sessions, setSessions] = useState<BakingSession[]>([]);
  const [localSelectedSession, setLocalSelectedSession] = useState<BakingSession | null>(null);

  useEffect(() => {
    setSessions(bakingSessions);
  }, [bakingSessions]);

  useEffect(() => {
    if (selectedSession) {
      setLocalSelectedSession(selectedSession);
    }
  }, [selectedSession]);

  const handleSessionUpdate = (sessionId: string, updates: Partial<BakingSession>) => {
    const updatedSessions = sessions.map(session =>
      session.id === sessionId
        ? { ...session, ...updates }
        : session
    );
    setSessions(updatedSessions);
    
    // Update local selected session
    if (localSelectedSession?.id === sessionId) {
      setLocalSelectedSession(prev => 
        prev ? { ...prev, ...updates } : null
      );
    }
  };
  
  const completedSessions = bakingSessions
    .filter(session => session.status === 'completed' && session.result)
    .sort((a, b) => {
      const dateA = new Date(a.result!.completedAt).getTime();
      const dateB = new Date(b.result!.completedAt).getTime();
      return dateB - dateA;
    })
    .slice(0, 6);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getProcessName = (processId: string) => {
    const process = FERMENTATION_PROCESSES.find(p => p.id === processId);
    return process ? process.name : processId;
  };

  const handleAnalyzeClick = (e: React.MouseEvent, session: BakingSession) => {
    e.stopPropagation(); // Prevent card click
    setSelectedSessionForAnalysis(session);
    
    // Check if session has already been analyzed
    const existingAnalysis = analyses.find(a => a.sessionId === session.id);
    if (existingAnalysis) {
      setShowAnalysisDetails(true);
    } else {
      setShowAnalysisModal(true);
    }
  };

  const handleMoreClick = (e: React.MouseEvent, session: BakingSession) => {
    e.stopPropagation(); // Prevent card click
    setSelectedSession(session);
  };

  if (completedSessions.length === 0) return null;

  return (
    <div className="p-1">
      <div className="flex items-center gap-2 mt-6 mb-3">
        <h2 className="text-2xl text-white font-brand font-light italic">Recent Results</h2>
      </div>

      <div className="space-y-4">
        {completedSessions.map((session) => {
          const hasAnalysis = analyses.some(a => a.sessionId === session.id);
          
          return (
            <div
              key={session.id}
              onClick={() => setSelectedSession(session)}
              className="bg-gradient-to-br from-[#636363] to-[#D8D8D8] overflow-hidden shadow-md h-[200px] hover:border hover:border-amber-400 hover:shadow-xl transition-shadow duration-200 cursor-pointer relative"
            >
              <div className="flex flex-col sm:flex-row h-full">
                {/* Left side - Session info */}
                <div className="p-4 hidden sm:block flex-col justify-between flex-grow relative">
                  <div>
                    <h3 className="text-2xl text-white font-brand font-light italic mb-1">{session.name}</h3>
                    <div className="space-y-2">
                      <div className="text-xs text-white italic">
                        <span className="font-medium">{formatDate(session.result!.completedAt)}</span>
                      </div>
                      <div className="bg-gradient-to-br from-[#535353] to-[#7A7979] text-white px-3 py-2 rounded-lg space-y-1">
                        <div className="text-sm">
                          <span className="opacity-70">Process:</span> {getProcessName(session.fermentationType)}
                        </div>
                        <div className="text-sm">
                          <span className="opacity-70">Oven setup:</span> {session.ovenType}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={(e) => handleMoreClick(e, session)}
                      className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-1 ml-auto"
                    >
                      MORE
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Right side - Image */}
                <div className="w-full sm:w-2/3 h-full relative flex-shrink-0">
                  <img
                    src={session.result!.thumbnailUrl || session.result!.imageUrl}
                    alt={`Result for ${session.name}`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="hidden sm:block absolute inset-y-0 left-0 w-[5px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none"></div>
                  <div className="hidden sm:block absolute inset-y-0 left-0 w-[30px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none"></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modals */}
      {localSelectedSession && (
        <ImageModal
          imageUrl={localSelectedSession.result!.imageUrl}
          title={localSelectedSession.name}
          session={localSelectedSession}
          onClose={() => {
            setSelectedSession(null);
            setLocalSelectedSession(null);
          }}
          onUpdate={handleSessionUpdate}
        />
      )}

      {showAnalysisModal && selectedSessionForAnalysis && (
        <AnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => {
            setShowAnalysisModal(false);
            setSelectedSessionForAnalysis(null);
          }}
          sessionId={selectedSessionForAnalysis.id}
        />
      )}

      {showAnalysisDetails && selectedSessionForAnalysis && (
        <AnalysisDetailsModal
          analysis={analyses.find(a => a.sessionId === selectedSessionForAnalysis.id)!}
          onClose={() => {
            setShowAnalysisDetails(false);
            setSelectedSessionForAnalysis(null);
          }}
        />
      )}
    </div>
  );
}